import { useClipboard, useTranslations } from "@jugl-web/utils";
import React, { useCallback, useState } from "react";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { environment } from "@web-src/environments/environment";
import { PlainButton, Tooltip, Alert } from "@jugl-web/ui-kit";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { resolveLiveChatAppSettings } from "@jugl-web/domain-resources/chat-bot/utils/resolveLiveChatAppSettings";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { CRMAccountCard } from "../CRMAccountCard";
import { CRMAccountTile } from "../CRMAccountTile";
import { ReactComponent as LiveChatIcon } from "./assets/live-chat.svg";
import { ReactComponent as LiveChat2Icon } from "./assets/live-chat-2.svg";
import { ReactComponent as ExternalIcon } from "./assets/external.svg";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";
import { ReactComponent as DownloadIcon } from "./assets/download.svg";
import { ReactComponent as EditIcon } from "./assets/edit.svg";
import { ReactComponent as MonitorIcon } from "./assets/monitor.svg";
import { ReactComponent as PenIcon } from "./assets/pen.svg";
import { ReactComponent as ShareIcon } from "./assets/share.svg";
import { ReactComponent as WorldIcon } from "./assets/world.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { useManageAccountsDrawer } from "../../providers/ManageAccountsDrawerContext";

interface LiveChatAppAccountCardProps {
  variant?: "card" | "tile";
  isClickable?: boolean;
}

const skipUrlProtocol = (url: string) => url.replace(/^https?:\/\//, "");

export const LiveChatAppAccountCard: React.FC<LiveChatAppAccountCardProps> = ({
  variant,
  isClickable,
}) => {
  const { t } = useTranslations();
  const { copyToClipboard } = useClipboard();
  const { entity, entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();

  const [
    updateLiveChatAppConfig,
    { isLoading: isUpdateLiveChatAppConfigLoading },
  ] = integrationsApi.useUpdateLiveChatAppConfigMutation();

  const { crmConfig } = useManageAccountsDrawer();
  const [isDisableLiveChatAlertOpen, setIsDisableLiveChatAlertOpen] =
    useState(false);

  const liveChatAppAccount = crmConfig?.livechat;

  const liveChatUrl = `${environment.liveChatUrl}/${entity.uniqueId}`;

  const toggleLiveChatAppAccount = useCallback(
    async (enabled: boolean) => {
      if (!liveChatAppAccount) return;

      await updateLiveChatAppConfig({
        entityId,
        enabled,
        meta: {
          successMessage: enabled
            ? t({
                id: "client-support-page.live-chat-via-link-is-enabled",
                defaultMessage: "Live Chat via Link is enabled",
              })
            : t({
                id: "client-support-page.live-chat-via-link-is-disabled",
                defaultMessage: "Live Chat via Link is disabled",
              }),
        },
      });
    },
    [entityId, liveChatAppAccount, t, updateLiveChatAppConfig]
  );

  if (variant === "tile") {
    const isEnabled = !!liveChatAppAccount?.enabled;

    const chatName = resolveLiveChatAppSettings(
      liveChatAppAccount?.settings
    ).businessName;

    const handleClick = () => {
      if (!isClickable || !liveChatAppAccount) {
        return;
      }

      transitionTo({ name: "liveChatAppAccount" });
    };

    return (
      <CRMAccountTile
        isClickable={isClickable}
        title={t({
          id: "client-support-page.live-chat-via-link",
          defaultMessage: "Live Chat via Link",
        })}
        isConnected={!!liveChatAppAccount}
        onClick={handleClick}
        icon={<LiveChat2Icon />}
        features={
          isEnabled
            ? [
                { icon: <EditIcon />, label: chatName },
                { icon: <WorldIcon />, label: skipUrlProtocol(liveChatUrl) },
              ]
            : [
                {
                  icon: <ShareIcon />,
                  label: t({
                    id: "client-support-page.live-chat-tile-feature-shareable-link",
                    defaultMessage: "Shareable link",
                  }),
                },
                {
                  icon: <PenIcon />,
                  label: t({
                    id: "client-support-page.live-chat-tile-feature-customize-appearance",
                    defaultMessage: "Customize appearance",
                  }),
                },
                {
                  icon: <DownloadIcon />,
                  label: t({
                    id: "client-support-page.live-chat-tile-feature-no-installation",
                    defaultMessage: "No installation needed",
                  }),
                },
                {
                  icon: <MonitorIcon />,
                  label: t({
                    id: "client-support-page.live-chat-tile-feature-any-device",
                    defaultMessage: "Works on any device",
                  }),
                },
              ]
        }
        status={
          liveChatAppAccount ? (isEnabled ? "enabled" : "disabled") : undefined
        }
      />
    );
  }

  return (
    <>
      <CRMAccountCard
        title={t({
          id: "client-support-page.live-chat-via-link",
          defaultMessage: "Live Chat via Link",
        })}
        onClick={
          isClickable
            ? () => transitionTo({ name: "liveChatAppAccount" })
            : undefined
        }
        icon={LiveChatIcon}
        description={t({
          id: "client-support-page.send-link-to-customer-start-conversation",
          defaultMessage: "Send a link to customer and start conversation",
        })}
        status={liveChatAppAccount?.enabled === true ? "enabled" : "disabled"}
        switchProps={{
          isChecked: liveChatAppAccount?.enabled === true,
          onChange: (isChecked) => {
            if (isChecked) {
              toggleLiveChatAppAccount(true);
              return;
            }

            setIsDisableLiveChatAlertOpen(true);
          },
          isDisabled: isUpdateLiveChatAppConfigLoading,
        }}
      >
        <div className="flex flex-col gap-6 max-lg:gap-4">
          <div className="h-px w-full bg-[#F2F2F2]" />
          <div className="flex flex-col gap-1">
            <span className="font-secondary text-lg font-medium text-black max-lg:text-base">
              {t({
                id: "client-support-page.live-chat-test-it-out",
                defaultMessage: "Test it out:",
              })}
            </span>
            <span className="font-secondary text-sm leading-[140%] tracking-[0.14px] text-dark-800">
              {t({
                id: "client-support-page.visit-live-chat-for-full-experience",
                defaultMessage:
                  "Visit your Live Chat to get full experience ✨",
              })}
            </span>
          </div>
          <div className="flex w-full max-w-125 items-center justify-between gap-3 rounded-2xl border border-solid border-dark-200 bg-grey-100 p-4 max-lg:p-3">
            <span className="min-w-0 cursor-text truncate font-secondary text-sm leading-[140%] tracking-[0.14px] text-dark select-text">
              {skipUrlProtocol(liveChatUrl)}
            </span>
            <div className="flex shrink-0 items-center gap-3">
              <Tooltip
                renderTrigger={({ props, ref }) => (
                  <PlainButton
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-solid border-grey-300 bg-white ring-grey-300 transition ring-inset hover:ring-1"
                    ref={ref}
                    onClick={() => window.open(liveChatUrl, "_blank")}
                    {...props}
                  >
                    <ExternalIcon />
                  </PlainButton>
                )}
              >
                {t({
                  id: "client-support-page.visit-page",
                  defaultMessage: "Visit Page",
                })}
              </Tooltip>
              <Tooltip
                renderTrigger={({ props, ref }) => (
                  <PlainButton
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-solid border-grey-300 bg-white ring-grey-300 transition ring-inset hover:ring-1"
                    ref={ref}
                    onClick={() => copyToClipboard(liveChatUrl)}
                    {...props}
                  >
                    <CopyIcon />
                  </PlainButton>
                )}
              >
                {t({
                  id: "client-support-page.copy-live-chat-link",
                  defaultMessage: "Copy Live Chat Link",
                })}
              </Tooltip>
            </div>
          </div>
        </div>
      </CRMAccountCard>
      <Alert
        isOpen={isDisableLiveChatAlertOpen}
        onRequestClose={() => setIsDisableLiveChatAlertOpen(false)}
        title={t({
          id: "client-support-page.disable-live-chat-title",
          defaultMessage: "Disable Live Chat",
        })}
        content={t({
          id: "client-support-page.disable-live-chat-description",
          defaultMessage:
            "If you will disable the Live Chat, Customer will no longer be able to reach out to you via shared Chat Link",
        })}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
            style: { flex: "none", width: "140px" },
          },
          {
            text: t({
              id: "common.disable",
              defaultMessage: "Disable",
            }),
            color: "primary",
            iconEnd: <ArrowIcon />,
            onClick: async (_, { closeAlert }) => {
              closeAlert();
              await toggleLiveChatAppAccount(false);
            },
          },
        ]}
      />
    </>
  );
};
